import React, { useEffect, useState } from 'react';

const LOGO_W     = 180;
const LOGO_H     = 200;
const LOGO_TOP   = 50;
const LOGO_CTR_Y = LOGO_TOP + Math.round(LOGO_H * 0.42); // gear visual centre

export default function CinematicSplash({ onDone }) {
  const [hiding, setHiding] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setHiding(true), 2600);
    const t2 = setTimeout(() => onDone(), 3100);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [onDone]);

  return (
    <div style={{ ...O.overlay, opacity: hiding ? 0 : 1, transition: hiding ? 'opacity 0.5s ease' : 'none' }}>
      <div style={O.bgAmbient} />
      <div style={O.bgAmbient2} />

      {/* Headline */}
      <div style={{ ...O.textBlock, animation: 'csTextIn 0.75s cubic-bezier(0.175,0.885,0.32,1.1) 0.1s both' }}>
        <div style={O.eyebrow}>ERP Intelligence Platform</div>
        <h1 style={O.headline}>
          Ask anything.<br />
          Know everything.
        </h1>
      </div>

      {/* Stage — logo floats freely, no card boundary */}
      <div style={O.stage}>

        {/* Expanding halo rings */}
        <div style={{ ...O.halo, width: 420, height: 420, animationDelay: '0.7s'  }} />
        <div style={{ ...O.halo, width: 280, height: 280, animationDelay: '1.2s'  }} />
        <div style={{ ...O.halo, width: 168, height: 168, animationDelay: '1.75s' }} />

        {/* Ambient glow beneath gear */}
        <div style={{ ...O.glow, animation: 'csFadeIn 0.6s ease 0.5s both' }} />

        {/* Logo — floats freely, no card, gear spins on reveal */}
        <div style={{ ...O.logoWrap, animation: 'csLogoIn 0.8s cubic-bezier(0.175,0.885,0.32,1.1) 0.35s both' }}>
          <img
            src="/logo.svg"
            alt="SolutionOne"
            style={{
              width: LOGO_W, height: 'auto', objectFit: 'contain',
              animation: 'csGearSpin 1.1s cubic-bezier(0.175,0.885,0.32,1.275) 0.35s both',
              filter: 'drop-shadow(0 0 22px rgba(27,94,59,0.75)) drop-shadow(0 10px 30px rgba(0,0,0,0.55))'
            }}
          />
        </div>

        {/* Light cone below gear */}
        <div style={{ ...O.lightCone, animation: 'csFadeIn 0.5s ease 0.9s both' }} />
      </div>

      <style>{`
        @keyframes csTextIn {
          from { opacity:0; transform:translateY(-34px); }
          to   { opacity:1; transform:translateY(0); }
        }
        @keyframes csLogoIn {
          from { opacity:0; transform:translateX(-50%) translateY(50px) scale(0.6); }
          to   { opacity:1; transform:translateX(-50%) translateY(0)    scale(1); }
        }
        @keyframes csGearSpin {
          from { opacity:0; transform: scale(0.6); }
          to   { opacity:1; transform: scale(1); }
        }
        @keyframes csFadeIn {
          from { opacity:0; }
          to   { opacity:1; }
        }
        @keyframes csHaloRipple {
          0%   { transform:translate(-50%,-50%) scale(0.45); opacity:0; }
          10%  { opacity:0.55; }
          100% { transform:translate(-50%,-50%) scale(1.32); opacity:0; }
        }
        @keyframes csAmbient {
          0%,100% { opacity:1; }
          50%     { opacity:0.5; }
        }
      `}</style>
    </div>
  );
}

const O = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 9999,
    background: 'linear-gradient(160deg, #020D05 0%, #041208 18%, #0A2E17 45%, #061A0C 75%, #020D05 100%)',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    overflow: 'hidden',
  },
  bgAmbient: {
    position: 'absolute', inset: 0, pointerEvents: 'none',
    background: 'radial-gradient(ellipse at 50% 62%, rgba(27,94,59,.32) 0%, transparent 58%)',
    animation: 'csAmbient 3s ease-in-out infinite',
  },
  bgAmbient2: {
    position: 'absolute', inset: 0, pointerEvents: 'none',
    background: 'radial-gradient(ellipse at 30% 20%, rgba(46,125,82,.12) 0%, transparent 55%)',
  },
  textBlock: {
    textAlign: 'center', position: 'relative', zIndex: 2, marginBottom: 52,
  },
  eyebrow: {
    fontSize: 11, fontWeight: 700, letterSpacing: '3px',
    color: '#4CAF7D', textTransform: 'uppercase', marginBottom: 14,
  },
  headline: {
    fontSize: 'clamp(38px, 6vw, 76px)', fontWeight: 800,
    color: '#fff', lineHeight: 1.08,
    margin: '0 0 16px', letterSpacing: '-1.5px',
  },
  stage: {
    position: 'relative', width: 340, height: 320,
  },
  halo: {
    position: 'absolute',
    top: LOGO_CTR_Y, left: '50%',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(27,94,59,.48) 0%, transparent 70%)',
    pointerEvents: 'none',
    animation: 'csHaloRipple 2.6s ease-out infinite',
    animationFillMode: 'both',
  },
  glow: {
    position: 'absolute',
    top: LOGO_CTR_Y - 60, left: '50%',
    transform: 'translate(-50%, 0)',
    width: 260, height: 260,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(27,94,59,.22) 0%, transparent 70%)',
    pointerEvents: 'none', zIndex: 1,
  },
  logoWrap: {
    position: 'absolute',
    top: LOGO_TOP, left: '50%',
    width: LOGO_W, height: LOGO_H,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 3,
    // intentionally no background, border, or backdropFilter — logo floats freely
  },
  lightCone: {
    position: 'absolute',
    top: LOGO_TOP + LOGO_H + 4, left: '50%',
    transform: 'translateX(-50%)',
    width: 240, height: 90,
    background: 'radial-gradient(ellipse at 50% 0%, rgba(27,94,59,.35) 0%, transparent 70%)',
    pointerEvents: 'none', zIndex: 2,
  },
};
